import React, { useState, useEffect } from "react";
import "react-cmdk/dist/cmdk.css";
import CommandPalette, { filterItems, getItemIndex } from "react-cmdk";
import { connect, useDispatch, useSelector } from "react-redux";
import { Responsive, Icon } from "semantic-ui-react";

import { showProductEditorModal, loadProductForEdit } from "./product/editor/actions";
import { searchInvoice } from "./invoice/grid/actions";
import { searchProduct } from "./product/search/actions";
import { setInvoiceBuilderCustomer } from "./invoice/builder/actions";
import { changeInvoiceGridState } from "./invoice/grid/actions";
import { history } from "./store";
import { CompanyFeatures } from "utilities/dist/company/constants";
import { buildDebugInfo, isMac } from "./common/helpers";
import { COMMAND_MENU } from "./common/constants";
import { isCompanyFeatureActive } from "utilities/dist/company/helpers";
import { buildInvoiceNumberWithBeer } from "utilities/dist/invoice/helpers";

const CommandPaletteComponent = ({ customerList, productList, minimized }) => {
    const { currentVersion, user, company } = useSelector(({ version, auth }) => ({
        currentVersion: version.current,
        user: auth.user,
        company: auth.user?.company,
    }));

    const showInventory = company?.companyFeatures?.find(
        feature => [CompanyFeatures.INVENTORY].includes(feature.name) && feature.active
    );

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [invoices, setInvoices] = useState([]);
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);

    const dispatch = useDispatch();

    const debugInfo = buildDebugInfo({ currentVersion, user });

    const debugInfoBody = [`Debug Info:%0D%0A${debugInfo}`, `Thanks`].join(`%0D%0A%0D%0A`);
    const reportIssueLink = `mailto:txwholesale@totalwine.com?subject=🐛 yDrink Invoice issue report&body=${debugInfoBody}`;

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.metaKey && e.key === "k") {
                e.preventDefault();
                e.stopPropagation();

                setOpen(currentValue => {
                    return !currentValue;
                });
            }
        }

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const filteredItems = filterItems(
        [
            ...COMMAND_MENU,
            ...(showInventory
                ? [
                      {
                          heading: "Inventories",
                          id: "inventories",
                          items: [
                              {
                                  id: "inventories",
                                  children: "Inventories",
                                  icon: "ServerStackIcon",
                                  href: "/inventory",
                              },
                          ],
                      },
                  ]
                : []),
            {
                heading: "Settings",
                id: "settings",
                items: [
                    {
                        id: "settings",
                        children: "Settings",
                        icon: "Cog6ToothIcon",
                        href: "/setting",
                    },
                    {
                        id: "settings-clear",
                        children: "Clear Cache",
                        icon: "FireIcon",
                        href: "/cache/clear",
                    },
                    {
                        id: "settings-report",
                        children: "Report Issue",
                        icon: "BugAntIcon",
                        href: reportIssueLink,
                    },
                ],
            },
        ],
        search
    );

    useEffect(() => {
        if (search.length > 2) {
            if (/^\d+$/.test(search)) {
                findInvoice();
            } else {
                findProduct();
                findCustomer();
            }
        } else {
            setInvoices([]);
            setCustomers([]);
            setProducts([]);
        }
    }, [search]);

    const findInvoice = async () => {
        const { rows } = await searchInvoice(search);
        if (rows.length > 0) {
            setInvoices(
                rows.slice(0, 3).map(row => ({
                    ...row,
                    displayNumber: buildInvoiceNumberWithBeer(
                        row,
                        isCompanyFeatureActive(company, CompanyFeatures.CUSTOMER_INVOICE_NUMBERING)
                    ),
                }))
            );
        }
    };

    const findProduct = async () => {
        if (productList.length > 0) {
            setProducts(
                productList
                    .filter(({ product }) => product.toLowerCase().includes(search.toLowerCase()))
                    .slice(0, 3)
            );
        } else {
            const data = await searchProduct(search);
            setProducts(data.slice(0, 3));
        }
    };

    const findCustomer = async () => {
        setCustomers(
            customerList
                .filter(({ name }) => name.toLowerCase().includes(search.toLowerCase()))
                .slice(0, 3)
        );
    };

    const openProductModal = product => {
        dispatch(loadProductForEdit(product));
        dispatch(showProductEditorModal(true));

        history.push("/product");
    };

    const createInvoice = customer => {
        dispatch(setInvoiceBuilderCustomer(customer));
        history.push("/invoice/create");
    };

    const viewInvoices = customer => {
        dispatch(changeInvoiceGridState({ customerId: customer.id }));
        history.push("/invoice");
    };

    if (!user) return null;
    return (
        <>
            <CommandPalette
                onChangeSearch={setSearch}
                onChangeOpen={setOpen}
                search={search}
                isOpen={open}
                page="root"
            >
                <CommandPalette.Page id="root">
                    {invoices && invoices.length > 0 && (
                        <CommandPalette.List key={"invoices"} heading={"Invoices"}>
                            {invoices.map((invoice, index) => (
                                <CommandPalette.ListItem
                                    key={`invoice-${invoice.id}`}
                                    index={1000 + index}
                                    children={`View invoice #${invoice.displayNumber}`}
                                    icon="ClipboardDocumentListIcon"
                                    href={`/invoice/${invoice.displayNumber}`}
                                />
                            ))}
                        </CommandPalette.List>
                    )}
                    {products && products.length > 0 && (
                        <CommandPalette.List key={"products"} heading={"Products"}>
                            {products.map((product, index) => (
                                <CommandPalette.ListItem
                                    key={`product-${product.id}`}
                                    index={2000 + index}
                                    children={`Edit product ${product.product} (${product.product_type} ${product.product_size})`}
                                    icon="TrophyIcon"
                                    onClick={() => openProductModal(product)}
                                />
                            ))}
                        </CommandPalette.List>
                    )}
                    {customers && customers.length > 0 && (
                        <CommandPalette.List key={"customers"} heading={"Customers"}>
                            {customers.map((customer, index) => (
                                <>
                                    <CommandPalette.ListItem
                                        key={`customer-${customer.id}`}
                                        index={3000 + index * 3}
                                        children={`Edit customer ${customer.name}`}
                                        icon="UsersIcon"
                                        href={`/customer/${customer.id}/edit`}
                                    />
                                    <CommandPalette.ListItem
                                        key={`customer-invoice-${customer.id}`}
                                        index={3001 + index * 3}
                                        children={`Create invoice for ${customer.name}`}
                                        icon="DocumentPlusIcon"
                                        onClick={() => createInvoice(customer)}
                                    />
                                    <CommandPalette.ListItem
                                        key={`customer-invoice-history-${customer.id}`}
                                        index={3002 + index * 3}
                                        children={`View invoices for ${customer.name}`}
                                        icon="ClipboardDocumentListIcon"
                                        onClick={() => viewInvoices(customer)}
                                    />
                                </>
                            ))}
                        </CommandPalette.List>
                    )}
                    {filteredItems.map(list => (
                        <CommandPalette.List key={list.id} heading={list.heading}>
                            {list.items.map(({ id, ...rest }) => (
                                <CommandPalette.ListItem
                                    key={id}
                                    index={getItemIndex(filteredItems, id)}
                                    {...rest}
                                />
                            ))}
                        </CommandPalette.List>
                    ))}
                </CommandPalette.Page>
            </CommandPalette>
            <Responsive
                as="a"
                {...Responsive.onlyComputer}
                className="hp-15 hm-10 sidenav-item sidenav-collapse clickable"
                onClick={() => setOpen(true)}
            >
                <Icon name="terminal" />
                {minimized ? "" : <span>{`${isMac ? "⌘" : "Ctrl"} + K`}</span>}
            </Responsive>
        </>
    );
};

const mapStateToProps = ({ auth, customerList, productGrid }) => ({
    user: auth.user,
    customerList: customerList.data,
    productList: productGrid.products,
});

export default connect(mapStateToProps)(CommandPaletteComponent);
